<template>
  <div>
    <div class="row">
      <div class="form-group col-md-6">
        <label class="mb-1"><b><u class="section-title"><i class="fas fa-graduation-cap"></i>&nbsp;&nbsp;Mi Progreso&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u></b></label>
      </div>
      <div class="col-md-12"><hr></div>
    </div>

    <b-overlay :show="loading" rounded="sm">

      <!-- Lo más accionable: avisar de los cursos vencidos -->
      <div v-if="expired.length" class="alert-expired">
        <i class="fas fa-exclamation-triangle"></i>
        <span v-if="expired.length === 1">
          Tu curso <b>{{ expired[0] }}</b> está vencido. Contacta con la escuela para renovarlo.
        </span>
        <span v-else>
          Tienes <b>{{ expired.length }}</b> cursos vencidos ({{ expired.join(', ') }}).
          Contacta con la escuela para renovarlos.
        </span>
      </div>

      <div class="kpi-grid">
        <div class="kpi-card">
          <div class="kpi-icon" :style="{ backgroundColor: colors.primary }"><i class="feather icon-book-open"></i></div>
          <div class="kpi-body">
            <div class="kpi-value">{{ kpis.courses || 0 }}</div>
            <div class="kpi-label">Cursos asignados</div>
          </div>
        </div>
        <div class="kpi-card">
          <div class="kpi-icon" :style="{ backgroundColor: colors.success }"><i class="feather icon-layers"></i></div>
          <div class="kpi-body">
            <div class="kpi-value">{{ kpis.modulesDone || 0 }} / {{ kpis.modulesTotal || 0 }}</div>
            <div class="kpi-label">Módulos completados</div>
          </div>
        </div>
        <div class="kpi-card">
          <div class="kpi-icon" :style="{ backgroundColor: colors.info }"><i class="far fa-clock"></i></div>
          <div class="kpi-body">
            <div class="kpi-value">{{ kpis.hours || '0m' }}</div>
            <div class="kpi-label">Tiempo dedicado</div>
          </div>
        </div>
        <div class="kpi-card">
          <div class="kpi-icon" :style="{ backgroundColor: daysColor }"><i class="far fa-calendar-alt"></i></div>
          <div class="kpi-body">
            <div class="kpi-value">{{ daysLabel }}</div>
            <div class="kpi-label">Vigencia más próxima</div>
          </div>
        </div>
      </div>

      <div class="row mt-2">
        <div class="col-lg-6" v-for="(course, i) in courses" :key="i">
          <div class="panel-block mb-2">
            <div class="panel-title">{{ course.course }}</div>

            <div class="row">
              <div class="col-5">
                <div :ref="'ring' + i"></div>
              </div>
              <div class="col-7">
                <p class="mb-1"><b>Módulos:</b> {{ course.modulesDone }} de {{ course.modulesTotal }}</p>
                <div class="mb-2">
                  <small><b>Lectura</b> · {{ course.readDone }} de {{ course.readRequired }}</small>
                  <div class="progress-mini"><div class="progress-mini-bar" :style="{ width: course.readPercent + '%' }"></div></div>
                </div>
                <div class="mb-2">
                  <small><b>Práctica</b> · {{ course.practiceDone }} de {{ course.practiceRequired }}</small>
                  <div class="progress-mini"><div class="progress-mini-bar" :style="{ width: course.practicePercent + '%' }"></div></div>
                </div>
                <small :class="course.daysLeft !== null && course.daysLeft < 0 ? 'text-danger' : 'text-muted'">
                  {{ course.startDate }} – {{ course.endDate }} · {{ vigencyLabel(course.daysLeft) }}
                </small>
              </div>
            </div>

            <div class="not-ready-list">
              <div class="not-ready-title">Módulos</div>
              <div v-for="(m, j) in course.modules" :key="j" class="not-ready-item">
                <span class="not-ready-name">{{ m.module }}</span>
                <span class="badge" :class="statusClass(m.status)">{{ m.statusLabel }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p v-if="!courses.length && !loading" class="text-muted">
        Todavía no tienes ningún curso asignado. Contacta con la escuela.
      </p>

    </b-overlay>
  </div>
</template>

<script>
import { BOverlay } from 'bootstrap-vue'

const COLORS = {
  primary: '#00b5b8',
  success: '#16d39a',
  info:    '#2dcee3',
  warning: '#ffa87d',
  danger:  '#ff7588',
}

export default {
  components: { BOverlay },
  data() {
    return {
      loading: false,
      courses: [],
      kpis: {},
      expired: [],
      charts: [],
      colors: COLORS,
    }
  },
  computed: {
    daysLabel() {
      const d = this.kpis.daysLeft
      if (d === null || d === undefined) return '—'
      if (d < 0) return 'Vencido'
      return d + ' días'
    },
    daysColor() {
      const d = this.kpis.daysLeft
      if (d === null || d === undefined) return COLORS.info
      if (d < 0) return COLORS.danger
      if (d <= 7) return COLORS.warning
      return COLORS.success
    },
  },
  created() {
    this.getProgress()
  },
  beforeDestroy() {
    this.destroyCharts()
  },
  methods: {
    /**
     * apexcharts.min.js se carga en el layout después de mix('js/app.js'),
     * así que al montar el componente window.ApexCharts aún no existe.
     */
    whenApexReady(callback) {
      if (window.ApexCharts) {
        callback()
      } else {
        window.addEventListener('load', callback, { once: true })
      }
    },
    vigencyLabel(days) {
      if (days === null) return 'Sin fecha'
      if (days < 0) return 'Vencido hace ' + Math.abs(days) + ' días'
      if (days === 0) return 'Vence hoy'
      return 'Quedan ' + days + ' días'
    },
    statusClass(status) {
      const classes = { 0: 'badge-secondary', 1: 'badge-warning', 2: 'badge-success' }
      return classes[status] !== undefined ? classes[status] : 'badge-secondary'
    },
    getProgress() {
      this.loading = true
      axios.get('/myProgress')
        .then(response => {
          const d = response.data.data
          this.courses = d.courses
          this.kpis = d.kpis
          this.expired = d.expired
          this.$nextTick(() => this.whenApexReady(this.renderRings))
        })
        .catch(error => { console.error(error) })
        .finally(() => { this.loading = false })
    },
    destroyCharts() {
      this.charts.forEach(c => { if (c) c.destroy() })
      this.charts = []
    },
    renderRings() {
      this.destroyCharts()

      this.courses.forEach((course, i) => {
        const ref = this.$refs['ring' + i]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (!el) return

        const chart = new window.ApexCharts(el, {
          chart: { type: 'radialBar', height: 190 , animations: { enabled: false }},
          series: [course.modulesPercent],
          labels: ['Avance'],
          colors: [course.modulesPercent >= 100 ? COLORS.success : COLORS.primary],
          plotOptions: {
            radialBar: {
              hollow: { size: '55%' },
              dataLabels: {
                name: { fontSize: '12px' },
                value: { fontSize: '20px', formatter: v => Math.round(v) + '%' },
              },
            },
          },
        })
        chart.render()
        this.charts.push(chart)
      })
    },
  }
}
</script>
